import { useQuery } from "@tanstack/react-query";
import { fetchMyManagerConfiguration } from "@src/apis/managerApis";
import { FEATURES } from "@src/constants";
import { useAuthUser, useRole } from "@src/hooks/useAuth";
import {
  MANAGER_ENTITLEMENTS,
  ManagerEntitlement,
} from "@src/types/managerConfiguration";
import { QUERY_KEYS } from "./queryKeys";

/**
 * What every manager could do before the backend served a configuration. A
 * manager falls back to this until it loads, and for good when it cannot be
 * loaded, so no manager loses anything while the endpoint is not live yet.
 */
const LEGACY_MANAGER_ENTITLEMENTS: readonly ManagerEntitlement[] = [
  MANAGER_ENTITLEMENTS.AUDIO_DOWNLOADS,
  MANAGER_ENTITLEMENTS.ADMIN_MESSAGES,
];

export const useManagerConfigurationQuery = () => {
  const role = useRole();
  const user = useAuthUser();

  return useQuery({
    // Keyed by user, so signing in as another manager never reads the last one's.
    queryKey: [QUERY_KEYS.MANAGER_CONFIGURATION, user?.id],
    queryFn: fetchMyManagerConfiguration,
    select: (response) => response.data.data,
    enabled: FEATURES.MANAGER_CONFIGURATIONS_ENABLED && role === "manager",
    staleTime: 1000 * 60 * 5,
    // A 404 means the backend has no manager configurations yet; don't retry.
    retry: false,
  });
};

/**
 * Whether the signed-in user may use `entitlement`. Only managers are limited
 * by it: an admin has everything, and a curator's own access is decided
 * elsewhere, so both always get `true`.
 */
export const useManagerEntitlement = (
  entitlement: ManagerEntitlement
): boolean => {
  const role = useRole();
  const { data, isSuccess } = useManagerConfigurationQuery();

  if (role !== "manager") return true;
  if (!isSuccess) return LEGACY_MANAGER_ENTITLEMENTS.includes(entitlement);
  return data?.entitlements?.includes(entitlement) ?? false;
};
